import { useCallback, useRef, useState } from 'react';
import { useTranslation } from '@sib/i18n';
import { FileItem } from '@sib/types';
import { Button, Card, Checkbox, Progress, Tooltip } from '@sib/ui';
import { ClosedEye, Done, Error, Eye, Trash } from '@sib/ui/icons';
import { useFileUploadItem } from './FileUploadItem.utils';

interface FileUploadItemProps {
  fileItem: FileItem;
  onUpdate: (updatedFileItem: FileItem) => void;
  onRemove: (fileId: string) => void;
  loading?: boolean;
  canUserMarkAsSensitive: boolean;
}

export const FileUploadItem = ({
  fileItem,
  onUpdate,
  onRemove,
  canUserMarkAsSensitive,
  loading = false,
}: FileUploadItemProps) => {
  const { t } = useTranslation();
  const {
    isUploaded,
    isError,
    isFileUploading,
    isFileDownloadable,
    getUploadPhase,
  } = useFileUploadItem(fileItem);
  const fileNameRef = useRef<HTMLParagraphElement>(null);
  const [isFileNameOverflowing, setIsFileNameOverflowing] = useState(false);

  const handleFileNameInteraction = useCallback(() => {
    const element = fileNameRef.current;
    if (!element) return;

    setIsFileNameOverflowing(
      element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
  }, []);

  return (
    <Card className="grid w-full gap-2 p-[14px]">
      <div className="flex w-full min-w-0 items-center justify-between gap-2 text-sm">
        <div className="flex min-w-0 flex-[1_1] items-center gap-2">
          <Checkbox
            checked={fileItem.selected}
            onCheckedChange={() =>
              onUpdate({ ...fileItem, selected: !fileItem.selected })
            }
            disabled={loading}
          />
          <div className="flex min-w-0 flex-[1_1] flex-col">
            <div className="flex min-w-0 items-center gap-2">
              {isUploaded ? (
                <Done className="text-secondary size-5 shrink-0" />
              ) : isError ? (
                <Error className="text-error-pink size-5 shrink-0" />
              ) : null}
              <Tooltip
                TooltipTriggerProps={{ asChild: true }}
                TooltipContentProps={{
                  className: 'max-w-[420px] wrap-break-word',
                }}
                content={isFileNameOverflowing && fileItem.file.name}
              >
                <p
                  ref={fileNameRef}
                  onMouseEnter={handleFileNameInteraction}
                  onFocus={handleFileNameInteraction}
                  className="text-muted-foreground min-w-0 flex-[1_1] truncate"
                >
                  {fileItem.file.name}
                </p>
              </Tooltip>
              {fileItem.errorMessage && (
                <p className="text-error-pink ml-auto text-xs break-all sm:text-base">
                  {fileItem.errorMessage}
                </p>
              )}
            </div>
            {isFileUploading && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Progress value={fileItem.progress} className="h-2 flex-1" />
                  <span className="text-muted-foreground text-xs">
                    {`${fileItem.progress}%`}
                  </span>
                </div>
                <span className="text-muted-foreground text-xs">
                  {getUploadPhase()}
                </span>
              </div>
            )}
          </div>
        </div>

        {!isUploaded && !isFileDownloadable && (
          <Button
            variant="ghost"
            onClick={() => onRemove(fileItem.id)}
            disabled={loading}
            type="button"
            className="p-1.5"
          >
            <Trash className="text-primary" />
          </Button>
        )}
        {canUserMarkAsSensitive && (
          <Tooltip
            content={
              fileItem.isSensitive
                ? t('fileManager.markAsSensitive.singleMarkTooltip.unMark')
                : t('fileManager.markAsSensitive.singleMarkTooltip.mark')
            }
            TooltipContentProps={{
              side: 'left',
              variant: 'secondary',
              className: 'text-sm max-w-[320px]',
            }}
            TooltipTriggerProps={{ asChild: true }}
          >
            <Button
              variant="ghost"
              onClick={() =>
                onUpdate({ ...fileItem, isSensitive: !fileItem.isSensitive })
              }
              disabled={loading}
              type="button"
              className="p-1.5"
            >
              {fileItem.isSensitive ? (
                <ClosedEye className="text-secondary size-6" />
              ) : (
                <Eye className="text-muted-foreground/60 size-6 hover:brightness-100" />
              )}
            </Button>
          </Tooltip>
        )}
      </div>
    </Card>
  );
};
